import React, { useEffect, useState, useCallback } from 'react';
import { Star, Send } from 'lucide-react';
import apiClient from '../services/apiClient';
import { toast } from 'sonner';
import { SAgentToastHeader } from './PendingCallNotifier';
// S-agent "review request ready" check-in. Appears when a wrapped event is held
// for owner confirmation (tenant "ask me first" on). Stays until answered.
// Mounted app-wide next to the S agent; shares the S-Agent toast shell.
export default function ReviewCheckinNotifier() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [bottomOffset, setBottomOffset] = useState(22);
  const load = useCallback(async () => {
    try {
      const r = await apiClient.get('/reviews/pending-checkins');
      const list = Array.isArray(r?.data) ? r.data : (Array.isArray(r?.data?.pending) ? r.data.pending : []);
      setItems(list);
    } catch (e) { /* quiet — retry next tick */ }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  // Guardrail: never overlap the call "email everyone?" toast. If one is on
  // screen, stack above it; otherwise sit at the corner.
  useEffect(() => {
    const measure = () => {
      try {
        const callEl = document.querySelector('[data-testid^="pending-notify-"]');
        if (callEl) {
          const h = callEl.getBoundingClientRect?.().height || 150;
          setBottomOffset(22 + h + 12);
        } else setBottomOffset(22);
      } catch (e) { setBottomOffset(22); }
    };
    measure();
    const t = setInterval(measure, 1000);
    return () => clearInterval(t);
  }, [items]);
  const approve = async (it) => {
    setBusyId(it.eventId);
    try {
      await apiClient.post(`/reviews/${it.eventId}/checkin/approve`);
      setItems((p) => p.filter((x) => x.eventId !== it.eventId));
      toast.success(`Review request sent${it.clientName ? ' — ' + it.clientName : ''}`);
    } catch (e) { toast.error('Could not send — try again'); }
    finally { setBusyId(null); }
  };
  const skip = async (it) => {
    setBusyId(it.eventId);
    try {
      await apiClient.post(`/reviews/${it.eventId}/checkin/skip`);
      setItems((p) => p.filter((x) => x.eventId !== it.eventId));
      toast(`Skipped${it.clientName ? ' — ' + it.clientName : ''} won't be asked`);
    } catch (e) { toast.error('Could not update — try again'); }
    finally { setBusyId(null); }
  };
  if (!items.length) return null;
  return (
    <div style={{ position: 'fixed', right: 22, bottom: bottomOffset, width: 360, maxWidth: 'calc(100vw - 32px)', zIndex: 10049, display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none' }}>
      {items.slice(0, 3).map((it) => (
        <div key={it.eventId} style={{ pointerEvents: 'auto', background: 'linear-gradient(160deg, rgba(20,26,42,0.99), rgba(12,17,32,0.99))', border: '1px solid rgba(212,175,55,0.35)', borderRadius: 16, padding: '16px 17px', boxShadow: '0 20px 55px rgba(0,0,0,0.55)', fontFamily: 'Figtree, system-ui, sans-serif' }} data-testid={`review-checkin-${it.eventId}`}>
          <SAgentToastHeader tag="stays until you answer" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 11 }}>
            <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(212,175,55,0.14)', border: '1px solid rgba(212,175,55,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F1D98A', flex: 'none' }}><Star className="w-3 h-3" style={{ fill: '#F1D98A' }} /></span>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Review request ready{it.clientName ? ' — ' + it.clientName : ''}</span>
          </div>
          <div style={{ fontSize: 13, color: '#aeb6c6', lineHeight: 1.5, margin: '9px 0 14px' }}>
            {it.clientName ? <b style={{ color: '#fff' }}>{it.clientName}'s</b> : 'Their'} {it.eventType || 'event'} just wrapped. Want me to send the review request, or skip it for this client?
          </div>
          <div style={{ display: 'flex', gap: 9 }}>
            <button onClick={() => approve(it)} disabled={busyId === it.eventId} style={{ flex: 1, fontSize: 13, fontWeight: 700, padding: 11, borderRadius: 11, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#F1D98A,#b8902f)', color: '#2a1f00', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, opacity: busyId === it.eventId ? 0.6 : 1 }} data-testid={`review-checkin-approve-${it.eventId}`}>
              <Send className="w-3.5 h-3.5" /> {busyId === it.eventId ? 'Sending…' : 'Yes, send it'}
            </button>
            <button onClick={() => skip(it)} disabled={busyId === it.eventId} style={{ flex: 1, fontSize: 13, fontWeight: 600, padding: 11, borderRadius: 11, cursor: 'pointer', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.14)', color: '#8A93A6' }} data-testid={`review-checkin-skip-${it.eventId}`}>No, skip it</button>
          </div>
        </div>
      ))}
    </div>
  );
}
